import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, DatePicker, TimePicker, message, Spin, Checkbox } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { ThemeButton, ThemeSelect } from '../components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  useCreateAppointmentMutation,
  useCalculatePriceMutation,
} from '../../services/appointments';
import { useLazyGetUsersQuery } from '../../services/users';
import { useGetServicesQuery } from '../../services/dogServiceApi';
import { useLazyGetDogByUserIDQuery } from '../../services/dogsApi';
import { EASTERN_TIMEZONE, sortDogsByName } from '../../helpers/helpers';
import MultiDateCalendar from '../multiDateCalendar/multiDateCalendar';
import { BOARDING_SERVICE_ID } from '../../constants/constants';

dayjs.extend(utc);
dayjs.extend(timezone);

const { RangePicker } = DatePicker;

const CreateAppointmentModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [selectedDogs, setSelectedDogs] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [markedDates, setMarkedDates] = useState({});
  const [checkInTime, setCheckInTime] = useState(
    dayjs().hour(7).minute(0).second(0)
  );
  const [checkOutTime, setCheckOutTime] = useState(
    dayjs().hour(19).minute(0).second(0)
  );
  const [isTimeValid, setIsTimeValid] = useState(true);
  const [loading, setLoading] = useState(false);

  const [createAppointment, { isLoading: isCreating }] =
    useCreateAppointmentMutation();

  const [fetchUsers, { data: usersData, isLoading: loadingUsers }] =
    useLazyGetUsersQuery();
  const userSearchTimerRef = useRef(null);

  // Load initial users when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUsers({ currentPage: 1, resultPerPage: 100 });
    }
    return () => {
      if (userSearchTimerRef.current) clearTimeout(userSearchTimerRef.current);
    };
  }, [isOpen, fetchUsers]);

  const { data: servicesData, isLoading: loadingServices } =
    useGetServicesQuery();

  const [getDogsByUserId, { data: dogsData, isLoading: loadingDogs }] =
    useLazyGetDogByUserIDQuery();

  // Fetch dogs when user is selected
  useEffect(() => {
    if (selectedUserId && step >= 5) {
      getDogsByUserId(selectedUserId);
    }
  }, [selectedUserId, step, getDogsByUserId]);

  const selectedService = servicesData?.data?.find(
    s => s._id === selectedServiceId
  );
  const isBoardingService = selectedServiceId === BOARDING_SERVICE_ID;

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedUserId(null);
      setSelectedServiceId(null);
      setSelectedDogs([]);
      setDateRange(null);
      setMarkedDates({});
      setCheckInTime(dayjs().hour(7).minute(0).second(0));
      setCheckOutTime(dayjs().hour(19).minute(0).second(0));
      setIsTimeValid(true);
    }
  }, [isOpen]);

  // Use backend calculatePrice endpoint for pricing (single source of truth)
  const [calculatePrice] = useCalculatePriceMutation();
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const debounceTimerRef = useRef(null);

  // Helper: build ISO strings for the price API
  const getEasternTimeISOForPrice = useCallback((dateStr, timeDate) => {
    if (!dateStr || !timeDate) return null;
    const hour = timeDate.hour();
    const minute = timeDate.minute();
    const dateTimeStr = `${dateStr} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
    return dayjs.tz(dateTimeStr, EASTERN_TIMEZONE).format();
  }, []);

  // Debounced call to backend calculatePrice
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    const hasDates = isBoardingService
      ? dateRange && dateRange[0] && dateRange[1]
      : Object.keys(markedDates).length > 0;

    if (!selectedService || !hasDates) {
      setPriceBreakdown(null);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      try {
        let startISO, endISO, appointmentDates;

        if (isBoardingService && dateRange) {
          const [start, end] = dateRange;
          startISO = getEasternTimeISOForPrice(
            start.format('YYYY-MM-DD'),
            checkInTime
          );
          endISO = getEasternTimeISOForPrice(
            end.format('YYYY-MM-DD'),
            checkOutTime
          );
          // Build dates array for boarding
          const datesArray = [];
          let current = start;
          while (current.isBefore(end) || current.isSame(end, 'day')) {
            datesArray.push(current.format('YYYY-MM-DD'));
            current = current.add(1, 'day');
          }
          appointmentDates = datesArray;
        } else {
          const sortedDates = Object.keys(markedDates).sort();
          startISO = getEasternTimeISOForPrice(sortedDates[0], checkInTime);
          endISO = getEasternTimeISOForPrice(
            sortedDates[sortedDates.length - 1],
            checkOutTime
          );
          appointmentDates = sortedDates;
        }

        const result = await calculatePrice({
          service_id: selectedServiceId,
          appointment_start_time: startISO,
          appointment_end_time: endISO,
          appointment_dates: appointmentDates,
          numberOfDogs: selectedDogs.length || 1,
          chargePerDay: selectedService?.price || 0,
          discount: 0,
        }).unwrap();

        if (result?.data) {
          setPriceBreakdown(result.data);
        }
      } catch (err) {
        console.error('Price calculation failed:', err);
      }
    }, 300);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [
    dateRange,
    markedDates,
    checkInTime,
    checkOutTime,
    isBoardingService,
    selectedDogs.length,
    selectedService,
    selectedServiceId,
    calculatePrice,
    getEasternTimeISOForPrice,
  ]);

  // Derive display values from priceBreakdown
  const daysOfService = priceBreakdown?.numberOfDays || 0;
  const extraCharges = priceBreakdown?.extraCharges || 0;
  const latePickupFee = priceBreakdown?.latePickupFee || 0;
  const serviceCost = priceBreakdown?.baseAmount || 0;
  const servicePrice = priceBreakdown?.subtotal || 0;
  const taxAmount = priceBreakdown?.taxAmount || 0;
  const totalAmount = priceBreakdown?.total || 0;
  const taxRate = priceBreakdown?.taxRate || 0;

  // Validate time ranges
  useEffect(() => {
    if (!selectedService || !checkInTime || !checkOutTime) {
      setIsTimeValid(true);
      return;
    }

    if (isBoardingService && dateRange) {
      const [start] = dateRange;
      if (!start) {
        setIsTimeValid(true);
        return;
      }

      const checkInHour = checkInTime.hour();
      const checkInMinute = checkInTime.minute();
      const checkOutHour = checkOutTime.hour();
      const checkOutMinute = checkOutTime.minute();

      // Boarding: Check-in 7am-12pm, Check-out before 7pm
      const checkInIsAfter7am = checkInHour >= 7;
      const checkInIsBeforeNoon =
        checkInHour < 12 || (checkInHour === 12 && checkInMinute === 0);
      const checkOutBefore7PM =
        checkOutHour < 19 || (checkOutHour === 19 && checkOutMinute === 0);

      setIsTimeValid(
        checkInIsAfter7am && checkInIsBeforeNoon && checkOutBefore7PM
      );
    } else if (Object.keys(markedDates).length > 0) {
      const checkInHour = checkInTime.hour();
      const checkInMinute = checkInTime.minute();
      const checkOutHour = checkOutTime.hour();
      const checkOutMinute = checkOutTime.minute();

      // Daycare: Check-in 7am-12pm, Check-out 12pm-7pm
      const checkInBetween7andNoon =
        checkInHour >= 7 &&
        (checkInHour < 12 || (checkInHour === 12 && checkInMinute === 0));
      const checkOutBetweenNoonAnd7PM =
        (checkOutHour > 12 || (checkOutHour === 12 && checkOutMinute > 0)) &&
        (checkOutHour < 19 || (checkOutHour === 19 && checkOutMinute === 0));

      setIsTimeValid(checkInBetween7andNoon && checkOutBetweenNoonAnd7PM);
    } else {
      setIsTimeValid(true);
    }
  }, [
    selectedService,
    checkInTime,
    checkOutTime,
    dateRange,
    markedDates,
    isBoardingService,
  ]);

  const handleDogToggle = dogId => {
    setSelectedDogs(prev =>
      prev.includes(dogId) ? prev.filter(id => id !== dogId) : [...prev, dogId]
    );
  };

  const handleDateRangeChange = dates => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      const start = dates[0];
      const end = dates[1];
      const datesArray = [];
      let current = start;
      while (current.isBefore(end) || current.isSame(end, 'day')) {
        datesArray.push(current.format('YYYY-MM-DD'));
        current = current.add(1, 'day');
      }
      const marked = {};
      datesArray.forEach(date => {
        marked[date] = { selected: true };
      });
      setMarkedDates(marked);
    } else {
      setMarkedDates({});
    }
  };

  const handleDateChange = date => {
    if (!date) {
      return;
    }
    const dateStr = date.format('YYYY-MM-DD');
    setMarkedDates(prev => {
      const newDates = { ...prev };
      if (newDates[dateStr]) {
        delete newDates[dateStr];
      } else {
        newDates[dateStr] = { selected: true };
      }
      return newDates;
    });
  };

  const removeDate = dateStr => {
    setMarkedDates(prev => {
      const newDates = { ...prev };
      delete newDates[dateStr];
      return newDates;
    });
  };

  const handleSubmit = async () => {
    if (!selectedUserId) {
      message.error('Please select a client');
      return;
    }
    if (!selectedServiceId) {
      message.error('Please select a service');
      return;
    }
    if (selectedDogs.length === 0) {
      message.error('Please select at least one dog');
      return;
    }
    if (isBoardingService && !dateRange) {
      message.error('Please select a date range');
      return;
    }
    if (!isBoardingService && Object.keys(markedDates).length === 0) {
      message.error('Please select at least one date');
      return;
    }
    if (!checkInTime || !checkOutTime) {
      message.error('Please select check-in and check-out times');
      return;
    }
    if (!isTimeValid) {
      message.error('Please select valid check-in and check-out times');
      return;
    }

    const sortedDates = Object.keys(markedDates).sort();

    // Helper function to create Eastern Time ISO string
    const getEasternTimeISO = (dateStr, timeDate) => {
      const hour = timeDate.hour();
      const minute = timeDate.minute();
      const dateTimeStr = `${dateStr} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
      return dayjs.tz(dateTimeStr, EASTERN_TIMEZONE).format();
    };

    let appointmentStartDate;
    let appointmentEndDate;
    let appointmentDates = [];

    if (isBoardingService && dateRange) {
      const [start, end] = dateRange;
      appointmentStartDate = getEasternTimeISO(
        start.format('YYYY-MM-DD'),
        checkInTime
      );
      appointmentEndDate = getEasternTimeISO(
        end.format('YYYY-MM-DD'),
        checkOutTime
      );
      let current = start;
      while (current.isBefore(end) || current.isSame(end, 'day')) {
        appointmentDates.push(current.format('YYYY-MM-DD'));
        current = current.add(1, 'day');
      }
    } else if (sortedDates.length > 0) {
      appointmentStartDate = getEasternTimeISO(sortedDates[0], checkInTime);
      appointmentEndDate = getEasternTimeISO(
        sortedDates[sortedDates.length - 1],
        checkOutTime
      );
      appointmentDates = sortedDates;
    }

    const appointmentData = {
      appointment: {
        user_id: selectedUserId,
        appointment_start_time: appointmentStartDate,
        appointment_end_time: appointmentEndDate,
        service_id: selectedServiceId,
        dogs: selectedDogs,
        priceAtAppoTime: serviceCost,
        chargePerDay: selectedService?.price || 0,
        skipAdminNotification: true,
        appointment_dates: appointmentDates,
      },
    };

    // Detect if appointment is backdated (compare in Eastern Time to match backend)
    const todayEastern = dayjs().tz(EASTERN_TIMEZONE).startOf('day');
    const isBackdated = isBoardingService
      ? dateRange &&
        dateRange[0] &&
        dayjs
          .tz(dateRange[0].format('YYYY-MM-DD'), EASTERN_TIMEZONE)
          .isBefore(todayEastern, 'day')
      : sortedDates.length > 0 &&
        dayjs
          .tz(sortedDates[0], EASTERN_TIMEZONE)
          .isBefore(todayEastern, 'day');

    // Show confirmation before creating
    Modal.confirm({
      title: isBackdated
        ? 'Confirm Backdated Appointment'
        : 'Confirm Appointment Creation',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: '16px', fontSize: '15px' }}>
            Are you sure you want to create this appointment?
          </p>
          <div
            style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: isBackdated ? '#fffbeb' : '#f0f9ff',
              borderRadius: '6px',
              border: `1px solid ${isBackdated ? '#fde68a' : '#bae6fd'}`,
            }}
          >
            <ul
              style={{
                margin: 0,
                paddingLeft: '24px',
                listStyleType: 'disc',
                color: isBackdated ? '#92400e' : '#0c4a6e',
                fontSize: '14px',
                lineHeight: '1.8',
              }}
            >
              {isBackdated ? (
                <>
                  <li style={{ marginBottom: '8px' }}>
                    No email notification will be sent to the client, but you
                    will be able to invoice them
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    The appointment status will automatically be set to{' '}
                    <strong>"accepted"</strong>
                  </li>
                </>
              ) : (
                <>
                  <li style={{ marginBottom: '8px' }}>
                    An email notification will be sent to the client
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    The appointment will be created with{' '}
                    <strong>"pending"</strong> status, you will need to manually
                    accept or reject this appointment after creation
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      ),
      okText: 'Yes, Create Appointment',
      cancelText: 'Cancel',
      okButtonProps: {
        style: { backgroundColor: '#10b981', borderColor: '#10b981' },
      },
      onOk: async () => {
        try {
          setLoading(true);
          await createAppointment(appointmentData).unwrap();
          message.success('Appointment created successfully');
          onSuccess?.();
          onClose();
        } catch (error) {
          console.error('Error creating appointment:', error);
          message.error(
            error?.data?.message ||
              error?.message ||
              'Failed to create appointment. Please try again.'
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const canProceedToNextStep = () => {
    switch (step) {
      case 1:
        return selectedUserId !== null;
      case 2:
        return selectedServiceId !== null;
      case 3:
        if (isBoardingService) {
          return dateRange !== null && dateRange[0] && dateRange[1];
        } else {
          return Object.keys(markedDates).length > 0;
        }
      case 4:
        return checkInTime !== null && checkOutTime !== null && isTimeValid;
      case 5:
        return selectedDogs.length > 0;
      default:
        return false;
    }
  };

  const canSubmit = () => {
    return (
      selectedUserId &&
      selectedServiceId &&
      selectedDogs.length > 0 &&
      ((isBoardingService && dateRange) ||
        (!isBoardingService && Object.keys(markedDates).length > 0)) &&
      checkInTime &&
      checkOutTime &&
      isTimeValid
    );
  };

  // Check if form has any data filled out
  const hasFormData = () => {
    return (
      selectedUserId ||
      selectedServiceId ||
      selectedDogs.length > 0 ||
      dateRange ||
      Object.keys(markedDates).length > 0 ||
      (checkInTime && checkInTime.hour() !== 7) ||
      (checkOutTime && checkOutTime.hour() !== 19)
    );
  };

  // Handle modal close with confirmation if form has data
  const handleClose = () => {
    if (hasFormData()) {
      Modal.confirm({
        title: 'Abandon Appointment Creation?',
        icon: <ExclamationCircleOutlined />,
        content:
          'You have unsaved changes. Are you sure you want to close this form? All entered data will be lost.',
        okText: 'Yes, Abandon',
        cancelText: 'Cancel',
        okButtonProps: { danger: true },
        onOk: () => {
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  const users = usersData?.data?.[0]?.users || [];
  const services = servicesData?.data || [];
  const allDogs = dogsData?.data?.data || dogsData?.data || [];
  // Filter to only show active dogs, listed alphabetically
  const dogs = sortDogsByName(allDogs.filter(dog => dog.active !== false));

  const userOptions = users.map(user => {
    const name = user.full_name || user.username || 'Unknown';
    const email = user.email || '';
    return {
      value: user._id,
      label: email ? `${name} (${email})` : name,
    };
  });

  const serviceOptions = services.map(service => ({
    value: service._id,
    label: service.title,
  }));

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={800}
      closable={true}
      title={
        <div className="flex items-center justify-between pt-4">
          <h2 className="text-xl font-semibold">Create New Appointment</h2>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map(s => (
              <div
                key={s}
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  s === step
                    ? 'bg-primary text-white'
                    : s < step
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s}
              </div>
            ))}
          </div>
        </div>
      }
    >
      <div className="mt-10">
        {/* Step 1: Select Client */}
        {step === 1 && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Select Client</h3>
            <ThemeSelect
              style={{ width: '100%' }}
              value={selectedUserId}
              onChange={setSelectedUserId}
              placeholder="Search and select a client"
              options={userOptions}
              loading={loadingUsers}
              showSearch
              filterOption={false}
              onSearch={value => {
                if (userSearchTimerRef.current)
                  clearTimeout(userSearchTimerRef.current);
                userSearchTimerRef.current = setTimeout(() => {
                  fetchUsers({
                    currentPage: 1,
                    resultPerPage: 100,
                    search: value || undefined,
                  });
                }, 300);
              }}
            />
            {selectedUserId && (
              <div className="mt-4 p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">
                  Selected:{' '}
                  <span className="font-semibold">
                    {users.find(u => u._id === selectedUserId)?.full_name ||
                      users.find(u => u._id === selectedUserId)?.username}
                  </span>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Service */}
        {step === 2 && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Select Service</h3>
            <ThemeSelect
              style={{ width: '100%' }}
              value={selectedServiceId}
              onChange={setSelectedServiceId}
              placeholder="Select a service"
              options={serviceOptions}
              loading={loadingServices}
            />
            {selectedService && (
              <div className="mt-4 p-4 bg-gray-50 rounded">
                <p className="font-semibold">{selectedService.title}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedService.description}
                </p>
                <p className="text-lg font-bold text-primary mt-2">
                  ${selectedService.price?.toFixed(2)} per dog /{' '}
                  {isBoardingService ? 'night' : 'day'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Select Dates */}
        {step === 3 && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Select Dates</h3>
            {isBoardingService ? (
              <div>
                <p className="mb-2 text-sm text-gray-600">
                  Select check-in and check-out dates (all times are in EST)
                </p>
                <RangePicker
                  style={{ width: '100%', height: 40 }}
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                />
                {dateRange && dateRange[0] && dateRange[1] && (
                  <div className="mt-4 p-3 bg-blue-50 rounded">
                    <p className="text-sm">
                      <span className="font-semibold">Nights:</span>{' '}
                      {daysOfService}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="mb-3 text-sm text-gray-600">
                  Click dates on the calendar to add or remove them (all times
                  are in EST)
                </p>
                <MultiDateCalendar
                  selectedDates={markedDates}
                  allowPastDates={true}
                  onToggleDate={dateStr => {
                    setMarkedDates(prev => {
                      const newDates = { ...prev };
                      if (newDates[dateStr]) {
                        delete newDates[dateStr];
                      } else {
                        newDates[dateStr] = { selected: true };
                      }
                      return newDates;
                    });
                  }}
                  onRemoveDate={removeDate}
                />
              </div>
            )}
          </div>
        )}

        {/* Step 4: Select Times */}
        {step === 4 && (
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Check-in & Check-out Times
            </h3>
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium">Check-in Time (ET)</p>
                <TimePicker
                  style={{ width: '100%', height: 40 }}
                  value={checkInTime}
                  onChange={setCheckInTime}
                  format="h:mm A"
                />
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Check-out Time (ET)</p>
                <TimePicker
                  style={{ width: '100%', height: 40 }}
                  value={checkOutTime}
                  onChange={setCheckOutTime}
                  format="h:mm A"
                />
              </div>
              {!isTimeValid && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-600">
                    {isBoardingService
                      ? '⚠️ Check-in must be between 7am-12pm, and check-out before 7pm'
                      : '⚠️ Check-in must be between 7am-12pm, and check-out between 12pm-7pm'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 5: Select Dogs */}
        {step === 5 && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Select Dogs</h3>
            {loadingDogs ? (
              <Spin />
            ) : dogs.length === 0 ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800">
                  No dogs found for this client. Please add dogs first.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {dogs.map(dog => (
                  <div
                    key={dog._id}
                    className={`p-3 border rounded cursor-pointer transition-colors ${
                      selectedDogs.includes(dog._id)
                        ? 'border-primary bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleDogToggle(dog._id)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedDogs.includes(dog._id)}
                        onChange={e => {
                          e.stopPropagation();
                          handleDogToggle(dog._id);
                        }}
                        onClick={e => e.stopPropagation()}
                      />
                      <img
                        src={dog.avatar}
                        alt={dog.dog_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <p className="font-semibold">{dog.dog_name}</p>
                        <p className="text-sm text-gray-600">{dog.breed}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedDogs.length > 0 && (
              <div className="mt-4 p-3 bg-green-50 rounded">
                <p className="text-sm">
                  <span className="font-semibold">Selected:</span>{' '}
                  {selectedDogs.length} dog{selectedDogs.length > 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Appointment Summary - visible on step 5 when all data is ready */}
        {step === 5 && canSubmit() && (
          <div className="mt-6 rounded-lg border border-gray-200 overflow-hidden">
            {/* Schedule Details */}
            <div
              style={{
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderBottom: '1px solid #e5e7eb',
              }}
              className="px-5 py-4"
            >
              <h3
                className="text-sm font-bold uppercase tracking-wider mb-3"
                style={{ color: '#92400e' }}
              >
                Schedule Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Left column: Dates */}
                <div
                  className="rounded-lg px-4 py-3"
                  style={{ backgroundColor: 'rgba(255,255,255,0.7)' }}
                >
                  {isBoardingService ? (
                    <>
                      <div className="mb-3">
                        <p
                          className="text-xs font-semibold uppercase tracking-wide mb-1"
                          style={{ color: '#78350f' }}
                        >
                          Drop-off
                        </p>
                        <p className="text-base font-bold text-gray-800">
                          {dateRange?.[0]?.format('ddd, MMM D, YYYY')}
                        </p>
                      </div>
                      <div>
                        <p
                          className="text-xs font-semibold uppercase tracking-wide mb-1"
                          style={{ color: '#78350f' }}
                        >
                          Pick-up
                        </p>
                        <p className="text-base font-bold text-gray-800">
                          {dateRange?.[1]?.format('ddd, MMM D, YYYY')}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <p
                        className="text-xs font-semibold uppercase tracking-wide mb-2"
                        style={{ color: '#78350f' }}
                      >
                        Selected Dates
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.keys(markedDates)
                          .sort()
                          .map(date => (
                            <span
                              key={date}
                              className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
                              style={{
                                backgroundColor: '#fac38b',
                                color: '#78350f',
                              }}
                            >
                              {dayjs(date).format('MMM D')}
                            </span>
                          ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Right column: Times */}
                <div
                  className="rounded-lg px-4 py-3"
                  style={{ backgroundColor: 'rgba(255,255,255,0.7)' }}
                >
                  <div className="mb-3">
                    <p
                      className="text-xs font-semibold uppercase tracking-wide mb-1"
                      style={{ color: '#78350f' }}
                    >
                      Check-in Time
                    </p>
                    <p className="text-base font-bold text-gray-800">
                      {checkInTime?.format('h:mm A')} ET
                    </p>
                  </div>
                  <div>
                    <p
                      className="text-xs font-semibold uppercase tracking-wide mb-1"
                      style={{ color: '#78350f' }}
                    >
                      Check-out Time
                    </p>
                    <p className="text-base font-bold text-gray-800">
                      {checkOutTime?.format('h:mm A')} ET
                    </p>
                  </div>
                </div>
              </div>

              {/* Duration badge */}
              <div className="mt-3 flex items-center gap-2">
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold"
                  style={{
                    backgroundColor: '#fac38b',
                    color: '#78350f',
                  }}
                >
                  {isBoardingService ? '🌙' : '☀️'} {daysOfService}{' '}
                  {isBoardingService
                    ? `night${daysOfService !== 1 ? 's' : ''}`
                    : `day${daysOfService !== 1 ? 's' : ''}`}
                </span>
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold"
                  style={{
                    backgroundColor: '#fac38b',
                    color: '#78350f',
                  }}
                >
                  🐕 {selectedDogs.length} dog
                  {selectedDogs.length !== 1 ? 's' : ''}
                </span>
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold"
                  style={{
                    backgroundColor: '#fac38b',
                    color: '#78350f',
                  }}
                >
                  {selectedService?.title}
                </span>
              </div>
            </div>

            {/* Pricing Summary */}
            <div className="px-5 py-4 bg-white">
              <h3
                className="text-sm font-bold uppercase tracking-wider mb-3"
                style={{ color: '#374151' }}
              >
                Pricing Summary
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Base Rate</span>
                  <span className="font-medium text-gray-800">
                    ${selectedService?.price?.toFixed(2)} /{' '}
                    {isBoardingService ? 'night' : 'day'}
                  </span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>
                    {isBoardingService ? 'Total Nights' : 'Total Days'}
                  </span>
                  <span className="font-medium text-gray-800">
                    {daysOfService}
                  </span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Dogs Selected</span>
                  <span className="font-medium text-gray-800">
                    {selectedDogs.length}
                  </span>
                </div>
                <div className="flex justify-between font-semibold text-gray-800 pt-1">
                  <span>Service Cost</span>
                  <span>${serviceCost.toFixed(2)}</span>
                </div>
                {latePickupFee > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Afternoon Pickup</span>
                    <span className="font-medium text-gray-800">
                      ${latePickupFee.toFixed(2)}
                    </span>
                  </div>
                )}
                {extraCharges > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Extra Charges</span>
                    <span className="font-medium text-gray-800">
                      ${extraCharges.toFixed(2)}
                    </span>
                  </div>
                )}
                <div
                  className="border-t pt-2 mt-2"
                  style={{ borderColor: '#e5e7eb' }}
                >
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span className="font-medium text-gray-800">
                      ${servicePrice.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600 mt-1">
                    <span>Tax (CT - {taxRate}%)</span>
                    <span className="font-medium text-gray-800">
                      ${taxAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div
                  className="border-t pt-3 mt-2"
                  style={{ borderColor: '#e5e7eb' }}
                >
                  <div className="flex justify-between items-baseline">
                    <span
                      className="text-base font-bold"
                      style={{ color: '#d97706' }}
                    >
                      Total Amount
                    </span>
                    <span
                      className="text-xl font-bold"
                      style={{ color: '#d97706' }}
                    >
                      ${totalAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <ThemeButton
            content="Cancel"
            onClick={handleClose}
            bgColor="!bg-gray-200"
            className="!text-gray-700 hover:!bg-gray-300"
          />
          <div className="flex gap-2">
            {step > 1 && (
              <ThemeButton
                content="Previous"
                onClick={() => setStep(step - 1)}
                bgColor="!bg-gray-200"
                className="!text-gray-700 hover:!bg-gray-300"
              />
            )}
            {step < 5 ? (
              <ThemeButton
                content="Next"
                onClick={() => setStep(step + 1)}
                disabled={!canProceedToNextStep()}
                bgColor="!bg-[#e8952f]"
                className="!text-white hover:!bg-[#d4841f]"
              />
            ) : (
              <ThemeButton
                content={
                  loading || isCreating ? <Spin /> : 'Create Appointment'
                }
                onClick={handleSubmit}
                disabled={!canSubmit() || loading || isCreating}
                bgColor="!bg-green-600"
                className="!text-white hover:!bg-green-700"
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateAppointmentModal;
